import { normalizeAiOutputFormat } from './index.js';
import { isPolarityLabelRows, listSurveyAxes, masterRowPolarity, } from './streetSurveyDisplay.js';
// ============================================================================
// 定数
// ============================================================================
/** 保存する `streetView.extraction.pipelineVersion`。出力の形を変えたら上げる。 */
export const STREET_SURVEY_PIPELINE_VERSION = 2;
/** `aiAnalysis.pipeline` に保存する識別子。 */
export const STREET_SURVEY_PIPELINE_ID = 'street-survey-v2';
/** 分類セット (位置検出の各件) の出力キー。既存の集計・表示と揃える。 */
export const STREET_SURVEY_ITEMS_KEY = 'classifications';
/** 地点の説明文の出力キー。 */
export const STREET_SURVEY_DESCRIPTION_KEY = 'description';
/** 方向の並び (`image_index` 0〜3)。撮影の基準方向から 0 / 90 / 180 / 270 度。 */
export const STREET_SURVEY_DIRECTION_LABELS = ['前方', '右側', '後方', '左側'];
export const STREET_SURVEY_DIRECTION_DEGREES = [0, 90, 180, 270];
export const STREET_SURVEY_PER_DIRECTION_LIMIT = { min: 1, max: 10, fallback: 3 };
export const STREET_SURVEY_TOTAL_LIMIT = { min: 1, max: 40 };
/** 許可値をJSON スキーマの列挙に入れる上限 (これを超えるマスタはプロンプトの一覧だけで示す)。 */
const JSON_SCHEMA_ENUM_LIMIT = 300;
/** プロンプトの文の中で、項目名に置き換える記号 (管理者が文を書き換えるときに使う)。 */
export const STREET_SURVEY_PLACEHOLDER_ANCHOR = '{位置の項目}';
export const STREET_SURVEY_PLACEHOLDER_EVALUATION = '{評価の項目}';
/**
 * 観察の指示の既定。調査の使命 (公正な視点とバランス感覚・課題の抽出も重要な使命) を冒頭に置く
 * (オーナー指示 2026-09-15、評価がポジティブに偏る対策、docs/98 R-96)。
 *
 * 2026-09-16 (docs/27 §17-5、B7): 特定の調査に寄らない文にした (以前の「都市環境と歩行環境の調査員」
 * 「歩行者の目線」は机上調査テンプレート「ウォーカブル向け」に移した)。調査の観点は位置検出の項目のマスタ
 * (定義・判断基準) とテンプレート / 地区の上書きの文で与える。
 */
export const DEFAULT_STREET_SURVEY_OBSERVATION_INSTRUCTION = [
    'あなたは、画像から場所の状況を記録する調査員です。',
    'この調査の目的は、下の項目の観点で、この場所の良い点と課題を記録することです。公正な視点とバランス感覚を持ち、見栄えや第一印象に引きずられず、課題を誇張もせず、画像から読み取れる事実に基づいて判断してください。',
    '良い点を見つけるだけでなく、損なわれている・不足している・危険といったネガティブな部分を見落とさずに抽出することも、この調査の重要な使命です。',
    'ストリートビュー、または 360 度写真から切り出した 1 方向の画像を観察してください。',
    '画像に写っている要素だけを根拠にし、写っていない要素を推測で補わないでください。',
].join('\n');
/**
 * 良い点と課題の探し方の既定 (付随項目に評価があるときだけ入る)。
 *
 * 2026-09-15 の比較で、この指示が無いと評価がポジティブに偏った (ネガティブ 20/62 件 → 2/38 件)。
 * 「課題も探す」を足しただけでは 5/54 件にとどまり (従来 25/65 件)、生の応答を見ると「要素を検出する」
 * 書き方のため写っている物 (歩道・庇) を良い資源として拾い、「狭い」「無い」状態の課題を拾えていなかった
 * (docs/98 R-96)。そこで 1 件を「話題とその場所での状態」の組み合わせとし、位置は根拠の部分を示す書き方にする。
 */
export const DEFAULT_STREET_SURVEY_EVALUATION_GUIDANCE = [
    `- 1 件は、写っている物そのものではなく、「${STREET_SURVEY_PLACEHOLDER_ANCHOR}」の話題と、その場所での状態（「${STREET_SURVEY_PLACEHOLDER_EVALUATION}」）の組み合わせとする。良し悪しは「${STREET_SURVEY_PLACEHOLDER_ANCHOR}」では表さず、「${STREET_SURVEY_PLACEHOLDER_EVALUATION}」で表す。`,
    `- 良い点と課題を同じ重さで探す。「無い」「足りない」「悪い」「壊れている」「見えにくい」「危ない」のような状態も課題として挙げる。課題も、その話題に当たる「${STREET_SURVEY_PLACEHOLDER_ANCHOR}」を選んだうえで「${STREET_SURVEY_PLACEHOLDER_EVALUATION}」を否定側の値にする。`,
    `- 同じ話題に良い点と課題の両方があれば、別の件として両方挙げる。`,
    `- 並べる順は重要さの順とし、課題を良い点より後回しにしない。`,
    `- 位置（box_2d）は、その判断の根拠が写っている部分を示す（課題なら、その状態が分かる部分）。`,
    `- 「${STREET_SURVEY_PLACEHOLDER_EVALUATION}」の中立は、良し悪しを判断する材料が無いときだけ使う。迷っただけで中立にしない。`,
].join('\n');
/**
 * 重複の判定の既定。2026-09-15 の比較で、選抜が「同じ基準コード」を重複として扱い、左右の歩道や
 * 地点で唯一の課題まで落とした (docs/98 R-96)。限定した後も、別の方向の中央付近の件を「同じ内容」として
 * まとめて落とした地点があったため、中央付近は重複にしないと明記する (オーナー指示 2026-09-15)。
 * 同じ課題が方向ごとに重なるのは、方向ごとの記録として残す (同)。
 */
export const DEFAULT_STREET_SURVEY_DUPLICATE_RULE = [
    '重複として扱うのは、隣り合う方向の境目などに同じ物・同じ場所が重ねて写り、同じ内容で検出されている場合だけとする。',
    '別の方向の画像の中央付近に写る要素は重複にしない（ほかの方向の候補と内容が似ていても、方向ごとの記録として残す）。',
    '同じ項目（同じ値）でも、道の反対側・別の建物・別の場所の要素や、評価が違うものは重複ではない。迷ったら重複にしない。',
].join('\n');
/**
 * 選抜プロンプトの冒頭の役割の文の既定 (docs/27 §17、テンプレート / 地区の上書きで変えられる)。
 * 2026-09-16 (B7): 特定の調査に寄らない文にした (以前の文は「ウォーカブル向け」テンプレートに移した)。
 */
export const DEFAULT_STREET_SURVEY_SELECTION_ROLE = 'あなたは、画像から場所の状況を記録する調査員です。';
/** 重要とみなす基準の既定 (2026-09-16 B7: 「環境の質（安全・快適・楽しさ）」を外し、調査の観点に置き換えた)。 */
export const DEFAULT_STREET_SURVEY_SELECTION_CRITERIA = 'この調査の観点で、この地点への影響が大きく、画像から根拠がはっきり読み取れる要素ほど重要とみなす。公正な視点とバランス感覚を持ち、良い点と課題を同じ重さで扱う。ネガティブな部分（課題）を明らかにすることもこの調査の重要な使命なので、課題を良い点より低く扱わない。';
/**
 * 地点の説明文の指示の既定 (画面の「既定」の表示用。プロンプトでは文字数が途中に入る、docs/98 R-98 中)。
 */
export const DEFAULT_STREET_SURVEY_DESCRIPTION_INSTRUCTION = '4 方向の画像全体から、この地点の環境を客観的に説明する。候補の一覧をなぞらず、地点全体の様子を書く。良い点と課題の両方があれば、偏らずに両方に触れる。';
/**
 * エリアの要約の指示の既定 (docs/98 R-98 中。以前は functions に「まちあるき調査」「3行程度」で固定)。
 * テンプレート / 地区の上書き (`extraction.areaSummaryInstruction`) で変えられる。
 */
export const DEFAULT_STREET_SURVEY_AREA_SUMMARY_INSTRUCTION = 'これらをもとに、このエリア全体の特徴を3行程度で簡潔にまとめてください。個々の地点の説明を並べ直すのではなく、エリア全体として共通する傾向や特徴を書いてください。';
/** エリアの要約のプロンプト (地点ごとの説明文を並べる)。 */
export function buildStreetSurveyAreaSummaryPrompt(instruction, pointTexts) {
    return [
        '以下は、机上調査で選択したエリアに含まれる複数の地点の分析結果です。',
        instruction.trim() || DEFAULT_STREET_SURVEY_AREA_SUMMARY_INSTRUCTION,
        '',
        pointTexts.map((t, i) => `地点${i + 1}: ${t}`).join('\n\n'),
    ].join('\n');
}
/** 出力フォーマットに説明文の文字数が無いときの目安。 */
const DEFAULT_DESCRIPTION_LENGTH = { min: 200, max: 300 };
/** 各件の理由の文字数の目安 (出力フォーマットの根拠列に長さが無いとき)。 */
const REASON_LENGTH_HINT = 100;
// ============================================================================
// 設定の解決
// ============================================================================
function clampInt(value, min, max) {
    if (typeof value !== 'number' || !Number.isFinite(value))
        return null;
    return Math.min(max, Math.max(min, Math.round(value)));
}
function trimmedOr(value, fallback) {
    return typeof value === 'string' && value.trim() ? value.trim() : fallback;
}
function indexSchemaFields(schema) {
    const normalized = normalizeAiOutputFormat(schema);
    const valueFromByKey = new Map();
    const itemKeys = new Set();
    const itemTextKeys = [];
    const itemTextLengths = new Map();
    let itemsMaxItems = null;
    let descriptionLength = null;
    const visit = (field, inItems) => {
        if (inItems) {
            itemKeys.add(field.key);
            if (field.kind === 'text') {
                itemTextKeys.push(field.key);
                if (typeof field.lengthMin === 'number' || typeof field.lengthMax === 'number') {
                    itemTextLengths.set(field.key, { min: field.lengthMin, max: field.lengthMax });
                }
            }
        }
        if ((field.kind === 'code-list-string' || field.kind === 'enum-array') &&
            field.source?.type === 'master') {
            valueFromByKey.set(field.key, field.source.valueFrom);
        }
        if (field.kind === 'text' && !inItems && field.key === STREET_SURVEY_DESCRIPTION_KEY) {
            descriptionLength = { min: field.lengthMin, max: field.lengthMax };
        }
        if (field.kind === 'object-array') {
            if (!inItems && itemsMaxItems === null && typeof field.maxItems === 'number') {
                itemsMaxItems = field.maxItems;
            }
            for (const child of field.itemFields ?? [])
                visit(child, true);
        }
    };
    for (const field of normalized.fields ?? [])
        visit(field, false);
    return {
        valueFromByKey,
        itemKeys,
        itemTextKeys,
        itemTextLengths,
        itemsMaxItems,
        descriptionLength,
    };
}
function toExtractionAxis(axis, index) {
    const nameField = axis.nameField && axis.nameField !== axis.codeField ? axis.nameField : null;
    return {
        key: axis.key,
        label: axis.label,
        codeField: axis.codeField,
        nameField,
        masterId: axis.masterId,
        valueFrom: index.valueFromByKey.get(axis.codeField) ?? null,
    };
}
function findAxis(axes, key) {
    if (!key)
        return null;
    return axes.find((a) => a.key === key || a.codeField === key) ?? null;
}
/**
 * テンプレ既定と地区上書きを項目ごとに重ねる (null / 未設定の項目はテンプレートに従う)。
 * 2. の空配列は「付けない」という明示の設定なので上書きとして扱う。
 */
export function mergeStreetSurveyExtractionDef(template, override) {
    if (!template && !override)
        return undefined;
    const out = { ...(template ?? {}) };
    for (const [key, value] of Object.entries(override ?? {})) {
        if (value !== undefined && value !== null)
            out[key] = value;
    }
    return out;
}
/**
 * v2 の設定を解決する (docs/27 §15-3)。
 *
 * @param schema 地区の上書きを反映した実効の出力フォーマット
 * @param display `resolveStreetSurveyDisplay` の結果 (テンプレ既定と地区上書きを重ねた extraction を含む)
 * @param options.legacyModeResults 旧方式の専用検証を使う地区 (v2 は使えない)
 */
export function resolveStreetSurveyExtraction(schema, display, options = {}) {
    const def = display?.extraction ?? {};
    const problems = [];
    const empty = {
        enabled: false,
        anchor: null,
        attached: [],
        perDirectionMaxItems: STREET_SURVEY_PER_DIRECTION_LIMIT.fallback,
        totalMaxItems: null,
        selectionCriteria: trimmedOr(def.selectionCriteria, DEFAULT_STREET_SURVEY_SELECTION_CRITERIA),
        selectionRole: trimmedOr(def.selectionRole, DEFAULT_STREET_SURVEY_SELECTION_ROLE),
        areaSummaryInstruction: trimmedOr(def.areaSummaryInstruction, DEFAULT_STREET_SURVEY_AREA_SUMMARY_INSTRUCTION),
        descriptionInstruction: typeof def.descriptionInstruction === 'string' && def.descriptionInstruction.trim()
            ? def.descriptionInstruction.trim()
            : null,
        observationInstruction: trimmedOr(def.observationInstruction, DEFAULT_STREET_SURVEY_OBSERVATION_INSTRUCTION),
        evaluationGuidance: trimmedOr(def.evaluationGuidance, DEFAULT_STREET_SURVEY_EVALUATION_GUIDANCE),
        duplicateMode: def.duplicateMode === 'none' ? 'none' : 'same-object',
        duplicateRule: trimmedOr(def.duplicateRule, DEFAULT_STREET_SURVEY_DUPLICATE_RULE),
        masterDetail: def.masterDetail === 'compact' ? 'compact' : 'full',
        includeMasterUsageRules: def.includeMasterUsageRules === true,
        reasonKey: 'reason',
        reasonLengthMax: null,
        evaluationField: display?.evaluationField?.trim() || null,
        descriptionLength: { ...DEFAULT_DESCRIPTION_LENGTH },
        candidates: [],
        problems,
    };
    if (!schema) {
        if (def.enabled)
            problems.push('出力フォーマットが見つからないため、新しい解析方式を使えません。');
        return empty;
    }
    const index = indexSchemaFields(schema);
    const rawAxes = listSurveyAxes(schema);
    const candidates = rawAxes.map((a) => toExtractionAxis(a, index)).filter((a) => !!a.masterId);
    const itemLevel = candidates.filter((a) => index.itemKeys.has(a.codeField));
    // 1. 位置検出の項目: 設定 → 主軸 (分類セットの子) → 分類セットの子の先頭 → 候補の先頭。
    const primaryKey = rawAxes.find((a) => a.role === 'primary')?.codeField;
    const anchor = findAxis(candidates, def.anchorAxisKey) ??
        itemLevel.find((a) => a.codeField === primaryKey) ??
        itemLevel[0] ??
        candidates[0] ??
        null;
    // 2. 各件に付ける項目: 設定 (空配列も尊重) → 分類セットの子にある 1. 以外。
    const attachedSource = Array.isArray(def.attachedAxisKeys)
        ? def.attachedAxisKeys.map((k) => findAxis(candidates, k)).filter((a) => !!a)
        : itemLevel;
    const attached = [];
    for (const axis of attachedSource) {
        if (anchor && axis.codeField === anchor.codeField)
            continue;
        if (attached.some((a) => a.codeField === axis.codeField))
            continue;
        attached.push(axis);
    }
    const perDirectionMaxItems = clampInt(def.perDirectionMaxItems, STREET_SURVEY_PER_DIRECTION_LIMIT.min, STREET_SURVEY_PER_DIRECTION_LIMIT.max) ??
        clampInt(index.itemsMaxItems, STREET_SURVEY_PER_DIRECTION_LIMIT.min, STREET_SURVEY_PER_DIRECTION_LIMIT.max) ??
        STREET_SURVEY_PER_DIRECTION_LIMIT.fallback;
    const totalMaxItems = clampInt(def.totalMaxItems, STREET_SURVEY_TOTAL_LIMIT.min, STREET_SURVEY_TOTAL_LIMIT.max);
    // 理由の列: 出力フォーマットの根拠列 (分類セットの子にあるとき) → 子の text 列で reason を含むもの。
    const reasoning = schema.reasoningField?.trim();
    const reasonKey = (reasoning && index.itemKeys.has(reasoning) ? reasoning : null) ??
        index.itemTextKeys.find((k) => /reason/i.test(k)) ??
        'reason';
    const desc = index.descriptionLength;
    const descriptionLength = {
        min: desc?.min ?? DEFAULT_DESCRIPTION_LENGTH.min,
        max: desc?.max ?? DEFAULT_DESCRIPTION_LENGTH.max,
    };
    const wantEnabled = def.enabled === true;
    if (wantEnabled) {
        if (options.legacyModeResults) {
            problems.push('この地区は旧方式の解析結果の検証を使っているため、新しい解析方式を使えません。');
        }
        if (!anchor) {
            problems.push('位置検出に使える項目がありません（マスタを参照する分類の項目が必要です）。');
        }
    }
    return {
        ...empty,
        enabled: wantEnabled && problems.length === 0,
        anchor,
        attached,
        perDirectionMaxItems,
        totalMaxItems,
        reasonKey,
        reasonLengthMax: index.itemTextLengths.get(reasonKey)?.max ?? null,
        descriptionLength,
        candidates,
        problems,
    };
}
// ============================================================================
// 値 (マスタの行 ↔ 出力値)
// ============================================================================
function resolveValueFrom(axis, master) {
    if (axis.valueFrom)
        return axis.valueFrom;
    return (master.rows ?? []).some((r) => r.majorCd) ? 'major-category-code' : 'category-code';
}
/** マスタの 1 行を出力値に変換する。 */
export function streetSurveyValueOfRow(row, valueFrom) {
    switch (valueFrom) {
        case 'category':
            return row.category;
        case 'major-code':
            return row.majorCd ?? row.categoryCd;
        case 'category-code':
            return row.categoryCd;
        case 'major-category-code':
        default:
            return row.majorCd ? `${row.majorCd}-${row.categoryCd}` : row.categoryCd;
    }
}
/** 項目の許可値 (マスタの行の順、重複なし)。マスタが無ければ空。 */
export function streetSurveyAllowedValues(axis, masters) {
    const master = axis.masterId ? masters.get(axis.masterId) : undefined;
    if (!master)
        return [];
    const valueFrom = resolveValueFrom(axis, master);
    const out = [];
    for (const row of master.rows ?? []) {
        const v = streetSurveyValueOfRow(row, valueFrom);
        if (v && !out.includes(v))
            out.push(v);
    }
    return out;
}
function suffixAfterHyphen(value) {
    const i = value.lastIndexOf('-');
    return i >= 0 ? value.slice(i + 1) : value;
}
/**
 * AI の出力値を許可値に正規化する。完全一致 → 前後の空白除去 → 末尾のコード (例 `GQL-01` の `01`) が
 * 一意に一致する許可値。いずれも無ければ null。
 */
export function normalizeStreetSurveyValue(raw, allowed) {
    if (typeof raw !== 'string' && typeof raw !== 'number')
        return null;
    const value = String(raw).trim();
    if (!value)
        return null;
    if (allowed.length === 0)
        return value;
    if (allowed.includes(value))
        return value;
    const suffix = suffixAfterHyphen(value);
    const matches = allowed.filter((a) => suffixAfterHyphen(a) === suffix);
    return matches.length === 1 ? matches[0] : null;
}
/** 出力値に対応するマスタの行 (名称の補完用)。 */
export function streetSurveyRowOfValue(axis, masters, value) {
    const master = axis.masterId ? masters.get(axis.masterId) : undefined;
    if (!master)
        return null;
    const valueFrom = resolveValueFrom(axis, master);
    return (master.rows ?? []).find((r) => streetSurveyValueOfRow(r, valueFrom) === value) ?? null;
}
/** 評価の列として明示した項目か。 */
function isConfiguredEvaluation(axis, evaluationField) {
    return !!evaluationField && (axis.key === evaluationField || axis.codeField === evaluationField);
}
/**
 * 項目が「評価」(良し悪しを表す) か。評価の列として明示した項目、マスタの行に極性の明示がある項目、
 * または行の名称がすべて評価の語の項目 (例: ポジティブ / ネガティブ / 中立、いいね / もったいない)。
 * 「危険箇所」「安心できる場所」などを一部の行に持つだけの対象物マスタは評価にしない (docs/98 R-98 中)。
 */
export function isStreetSurveyEvaluationAxis(axis, masters, evaluationField) {
    const master = axis.masterId ? masters.get(axis.masterId) : undefined;
    const rows = master?.rows ?? [];
    if (rows.length === 0)
        return false;
    if (isConfiguredEvaluation(axis, evaluationField))
        return true;
    if (rows.some((r) => r.polarity))
        return true;
    return isPolarityLabelRows(rows);
}
// ============================================================================
// プロンプト
// ============================================================================
function describeValueFormat(axis, master, allowed) {
    const valueFrom = resolveValueFrom(axis, master);
    const sample = allowed[0] ?? '';
    const kind = valueFrom === 'category' ? '名称' : 'コード';
    return `値は下の一覧の左側の${kind}をそのまま 1 つ書く（例: ${sample}）。`;
}
/**
 * マスタを机上調査用に書き出す (定義文と行。利用ルールは `includeUsageRules` のときだけ、docs/27 §15-2)。
 */
export function formatStreetSurveyMaster(axis, masters, detail, heading, includeUsageRules = false) {
    const master = axis.masterId ? masters.get(axis.masterId) : undefined;
    if (!master)
        return `【${heading}: ${axis.label}】\n（マスタが見つかりません）`;
    const valueFrom = resolveValueFrom(axis, master);
    const allowed = streetSurveyAllowedValues(axis, masters);
    const lines = [`【${heading}: ${axis.label}】`];
    if (master.definition?.trim())
        lines.push(master.definition.trim());
    if (includeUsageRules && master.usageRules?.trim()) {
        lines.push(`使い方: ${master.usageRules.trim()}`);
    }
    lines.push(describeValueFormat(axis, master, allowed));
    for (const row of master.rows ?? []) {
        const value = streetSurveyValueOfRow(row, valueFrom);
        const name = row.major && valueFrom !== 'category' ? `${row.major}／${row.category}` : row.category;
        if (detail === 'compact') {
            lines.push(valueFrom === 'category' ? `- ${value}` : `- ${value}: ${name}`);
            continue;
        }
        const example = row.example?.trim() ? `（${row.example.trim()}）` : '';
        lines.push(valueFrom === 'category' ? `- ${value}${example}` : `- ${value}: ${name}${example}`);
        if (row.criteria?.trim())
            lines.push(`  判断基準: ${row.criteria.trim()}`);
        if (row.definition?.trim())
            lines.push(`  定義: ${row.definition.trim()}`);
    }
    return lines.join('\n');
}
/**
 * 評価の項目の否定側・肯定側の値と例 (マスタの行の極性の明示 → 名称からの推定、の順)。
 */
export function streetSurveyPolarityValues(axis, masters, evaluationField) {
    const out = {
        negative: null,
        positive: null,
        negativeExample: null,
        positiveExample: null,
    };
    const master = axis.masterId ? masters.get(axis.masterId) : undefined;
    if (!master)
        return out;
    const valueFrom = resolveValueFrom(axis, master);
    const allowInference = isConfiguredEvaluation(axis, evaluationField) || isPolarityLabelRows(master.rows);
    for (const row of master.rows ?? []) {
        const polarity = masterRowPolarity(master, row, allowInference);
        const example = row.example?.trim() || null;
        if (polarity === 'negative' && out.negative === null) {
            out.negative = streetSurveyValueOfRow(row, valueFrom);
            out.negativeExample = example;
        }
        else if (polarity === 'positive' && out.positive === null) {
            out.positive = streetSurveyValueOfRow(row, valueFrom);
            out.positiveExample = example;
        }
    }
    return out;
}
/**
 * 出力例の件。評価の項目があるときは「課題の件」→「良い点の件」の 2 件を示す (出力例の評価が肯定側だけ
 * だと AI が肯定側に寄る、docs/98 R-96)。
 */
function exampleItems(cfg, masters, evaluation) {
    const anchorValues = cfg.anchor ? streetSurveyAllowedValues(cfg.anchor, masters) : [];
    const polarity = evaluation
        ? streetSurveyPolarityValues(evaluation, masters, cfg.evaluationField)
        : null;
    const build = (rank, anchorValue, evaluationValue, reason, box, distance) => {
        const item = { rank };
        if (cfg.anchor)
            item[cfg.anchor.codeField] = anchorValue;
        for (const axis of cfg.attached) {
            item[axis.codeField] =
                evaluation && axis.codeField === evaluation.codeField && evaluationValue !== null
                    ? evaluationValue
                    : (streetSurveyAllowedValues(axis, masters)[0] ?? '');
        }
        item[cfg.reasonKey] = reason;
        item.box_2d = box;
        item.distance_meters = distance;
        return item;
    };
    if (polarity?.negative && polarity.positive) {
        return [
            build(1, anchorValues[0] ?? '', polarity.negative, '課題の根拠（画像から読み取れる具体的な状態）', [520, 300, 990, 620], 4),
            build(2, anchorValues[1] ?? anchorValues[0] ?? '', polarity.positive, '良い点の根拠（画像から読み取れる具体的な状態）', [320, 650, 780, 980], 9),
        ];
    }
    return [
        build(1, anchorValues[0] ?? '', null, '画像から読み取れる具体的な根拠', [320, 180, 780, 540], 8),
    ];
}
/** 管理者が書いた文の `{位置の項目}` / `{評価の項目}` を項目名に置き換える。 */
export function fillStreetSurveyPlaceholders(text, anchorLabel, evaluationLabel) {
    return text
        .split(STREET_SURVEY_PLACEHOLDER_ANCHOR)
        .join(anchorLabel)
        .split(STREET_SURVEY_PLACEHOLDER_EVALUATION)
        .join(evaluationLabel);
}
/**
 * 方向別の検出プロンプト (docs/27 §15-4、A1〜A4)。4 方向で違うのは「この画像」の段落だけ。
 */
export function buildStreetSurveyDirectionPrompt(cfg, masters, directionIndex) {
    const label = STREET_SURVEY_DIRECTION_LABELS[directionIndex] ?? `方向${directionIndex + 1}`;
    const degree = STREET_SURVEY_DIRECTION_DEGREES[directionIndex] ?? 0;
    const anchorLabel = cfg.anchor?.label ?? '分類';
    const parts = [cfg.observationInstruction];
    parts.push([
        '【この画像】',
        `この地点の「${label}」方向（撮影の基準方向から ${degree} 度）の 1 枚です。`,
        'ほかの方向は別に解析するので、この画像に写っている範囲だけを対象にしてください。',
    ].join('\n'));
    if (cfg.anchor) {
        parts.push(formatStreetSurveyMaster(cfg.anchor, masters, cfg.masterDetail, '位置を検出する項目', cfg.includeMasterUsageRules));
    }
    for (const axis of cfg.attached) {
        parts.push(formatStreetSurveyMaster(axis, masters, cfg.masterDetail, '各件に付ける項目', cfg.includeMasterUsageRules));
    }
    const rules = [
        '【検出のしかた】',
        `- この画像から「${anchorLabel}」に当てはまる要素を、当てはまる順に最大 ${cfg.perDirectionMaxItems} 件検出する。当てはまる要素が無ければ空の配列にする。`,
        '- 1 件ごとに、画像内の位置（box_2d）と撮影地点からの推定距離（distance_meters）を付ける。',
        '- 同じ要素を 2 件に分けない。同じ種類でも画像内の別の場所に写っていれば別の件にする。',
    ];
    for (const axis of cfg.attached) {
        rules.push(`- 各件の「${axis.label}」は、画像全体の印象ではなく、その件の要素について判断する。`);
    }
    rules.push(cfg.reasonLengthMax !== null
        ? `- ${cfg.reasonKey} には、画像から読み取れる具体的な根拠を ${cfg.reasonLengthMax} 文字以内で書く。`
        : `- ${cfg.reasonKey} には、画像から読み取れる具体的な根拠を ${REASON_LENGTH_HINT} 文字程度で書く。`);
    parts.push(rules.join('\n'));
    // 付随項目に評価があるときだけ「良い点と課題の探し方」を入れる (docs/98 R-96 の評価の偏り対策)。
    const evaluation = cfg.attached.find((a) => isStreetSurveyEvaluationAxis(a, masters, cfg.evaluationField));
    if (evaluation) {
        const guidance = [
            '【良い点と課題の探し方】',
            fillStreetSurveyPlaceholders(cfg.evaluationGuidance, anchorLabel, evaluation.label),
        ];
        // 評価マスタの行の「該当例」を、課題 / 良い点の具体例として添える (マスタで直せるデータ駆動の例)。
        const polarity = streetSurveyPolarityValues(evaluation, masters, cfg.evaluationField);
        if (polarity.negative && polarity.negativeExample) {
            guidance.push(`- 課題の例（${polarity.negative}）: ${polarity.negativeExample}`);
        }
        if (polarity.positive && polarity.positiveExample) {
            guidance.push(`- 良い点の例（${polarity.positive}）: ${polarity.positiveExample}`);
        }
        parts.push(guidance.join('\n'));
    }
    const example = { [STREET_SURVEY_ITEMS_KEY]: exampleItems(cfg, masters, evaluation) };
    const fieldLines = [
        '【出力形式】',
        JSON.stringify(example, null, 2),
        '',
        `- rank: 1 から始まる連番（1 が最も当てはまる）。`,
    ];
    if (cfg.anchor) {
        fieldLines.push(`- ${cfg.anchor.codeField}: 「${cfg.anchor.label}」の一覧の値を 1 つ。`);
    }
    for (const axis of cfg.attached) {
        fieldLines.push(`- ${axis.codeField}: 「${axis.label}」の一覧の値を 1 つ。`);
    }
    fieldLines.push(`- ${cfg.reasonKey}: 根拠（日本語）。`, '- box_2d: [ymin, xmin, ymax, xmax]。画像の左上を 0、右下を 1000 とした整数 4 個。', '- distance_meters: 撮影地点からの推定距離（メートル、数値）。');
    parts.push(fieldLines.join('\n'));
    parts.push('【出力のルール】\n出力は JSON オブジェクト 1 個のみ。キーは上の形式にあるものだけを使う。');
    return parts.join('\n\n');
}
/** 方向別検出の JSON スキーマ (構造化出力、B5)。 */
export function buildStreetSurveyDirectionJsonSchema(cfg, masters) {
    const properties = {
        rank: { type: 'integer', minimum: 1 },
    };
    const required = ['rank'];
    for (const axis of [cfg.anchor, ...cfg.attached]) {
        if (!axis)
            continue;
        const allowed = streetSurveyAllowedValues(axis, masters);
        properties[axis.codeField] =
            allowed.length > 0 && allowed.length <= JSON_SCHEMA_ENUM_LIMIT
                ? { type: 'string', enum: allowed }
                : { type: 'string' };
        required.push(axis.codeField);
    }
    properties[cfg.reasonKey] = { type: 'string' };
    properties.box_2d = {
        type: 'array',
        items: { type: 'integer', minimum: 0, maximum: 1000 },
        minItems: 4,
        maxItems: 4,
    };
    properties.distance_meters = { type: 'number', minimum: 0 };
    required.push(cfg.reasonKey, 'box_2d', 'distance_meters');
    return {
        type: 'object',
        properties: {
            [STREET_SURVEY_ITEMS_KEY]: {
                type: 'array',
                maxItems: cfg.perDirectionMaxItems,
                items: { type: 'object', properties, required },
            },
        },
        required: [STREET_SURVEY_ITEMS_KEY],
    };
}
function toBox2d(raw) {
    if (!Array.isArray(raw) || raw.length !== 4)
        return null;
    const nums = raw.map(Number);
    if (!nums.every(Number.isFinite))
        return null;
    return nums.map((n) => Math.min(1000, Math.max(0, Math.round(n))));
}
/**
 * 方向別の応答 (解析できなかった方向は null) から候補を集める。1. の値が許可値に無い件は捨てる。
 * 方向内は rank 昇順で、1 方向あたりの上限を超えた件は捨てる。
 */
export function collectStreetSurveyCandidates(perDirection, cfg, masters) {
    if (!cfg.anchor)
        return [];
    const anchorAllowed = streetSurveyAllowedValues(cfg.anchor, masters);
    const attachedAllowed = cfg.attached.map((a) => streetSurveyAllowedValues(a, masters));
    const out = [];
    perDirection.forEach((parsed, imageIndex) => {
        if (!parsed || imageIndex > 3)
            return;
        const raw = parsed[STREET_SURVEY_ITEMS_KEY];
        const items = (Array.isArray(raw) ? raw : [])
            .filter((x) => !!x && typeof x === 'object')
            .map((x, i) => ({ x, rank: Number.isFinite(Number(x.rank)) ? Number(x.rank) : i + 1 }))
            .sort((a, b) => a.rank - b.rank);
        let kept = 0;
        for (const { x } of items) {
            if (kept >= cfg.perDirectionMaxItems)
                break;
            const anchorValue = normalizeStreetSurveyValue(x[cfg.anchor.codeField], anchorAllowed);
            if (!anchorValue)
                continue;
            const values = { [cfg.anchor.codeField]: anchorValue };
            cfg.attached.forEach((axis, i) => {
                values[axis.codeField] =
                    normalizeStreetSurveyValue(x[axis.codeField], attachedAllowed[i]) ?? '';
            });
            const distance = Number(x.distance_meters);
            kept += 1;
            out.push({
                id: `C${out.length + 1}`,
                imageIndex: imageIndex,
                directionRank: kept,
                values,
                reason: typeof x[cfg.reasonKey] === 'string' ? x[cfg.reasonKey].trim() : '',
                box2d: toBox2d(x.box_2d),
                distanceMeters: Number.isFinite(distance) && distance >= 0 ? distance : null,
            });
        }
    });
    return out;
}
// ============================================================================
// 選抜
// ============================================================================
/** 画像内の横位置 (box_2d の中心) を言葉にする。重複の判定 (方向の境目かどうか) の手掛かり。 */
function describeHorizontalPosition(box) {
    if (!box)
        return null;
    const center = (box[1] + box[3]) / 2;
    if (center < 333)
        return '画像の左寄り';
    if (center > 667)
        return '画像の右寄り';
    return '画像の中央';
}
function describeCandidate(c, cfg, masters) {
    const parts = [];
    for (const axis of [cfg.anchor, ...cfg.attached]) {
        if (!axis)
            continue;
        const value = c.values[axis.codeField] ?? '';
        if (!value)
            continue;
        const row = streetSurveyRowOfValue(axis, masters, value);
        const name = row && row.category !== value ? ` ${row.category}` : '';
        parts.push(`${axis.label}: ${value}${name}`);
    }
    const where = [
        STREET_SURVEY_DIRECTION_LABELS[c.imageIndex],
        `方向内 ${c.directionRank} 位`,
        describeHorizontalPosition(c.box2d),
        c.distanceMeters !== null ? `約 ${Math.round(c.distanceMeters)}m` : null,
    ]
        .filter((x) => !!x)
        .join('・');
    const reason = c.reason ? ` ／ 根拠: ${c.reason}` : '';
    return `- ${c.id}（${where}）${parts.join(' / ')}${reason}`;
}
/** 選抜のプロンプト (docs/27 §15-4、C11)。画像は前方・右側・後方・左側の順に添付する。 */
export function buildStreetSurveySelectionPrompt(cfg, masters, candidates) {
    const { min, max } = cfg.descriptionLength;
    const dedupe = cfg.duplicateMode === 'same-object';
    const parts = [
        [
            cfg.selectionRole,
            `同じ地点の 4 方向（${STREET_SURVEY_DIRECTION_LABELS.join('・')}の順）の画像と、方向ごとに検出した候補の一覧を渡します。`,
        ].join('\n'),
        [
            '【候補】',
            ...(candidates.length > 0
                ? candidates.map((c) => describeCandidate(c, cfg, masters))
                : ['（候補はありません）']),
        ].join('\n'),
        `【重要とみなす基準】\n${cfg.selectionCriteria}`,
    ];
    if (dedupe)
        parts.push(`【重複の判定】\n${cfg.duplicateRule}`);
    const steps = ['【やること】'];
    let n = 1;
    if (dedupe) {
        steps.push(`${n++}. 4 方向の画像を見比べ、【重複の判定】に当たる候補があれば、残す 1 件以外の ID を duplicates に入れる（当たらなければ空の配列）。`, `${n++}. 残った候補を、地点全体で重要な順に並べて ranking に入れる（残った候補の ID をすべて 1 回ずつ。duplicates の ID は入れない）。`);
    }
    else {
        steps.push(`${n++}. すべての候補を、地点全体で重要な順に並べて ranking に入れる（候補の ID をすべて 1 回ずつ。候補を減らさない）。`);
    }
    if (cfg.totalMaxItems !== null) {
        steps.push(`${n++}. 最終的に使うのは ranking の上位 ${cfg.totalMaxItems} 件なので、順位は慎重に決める。`);
    }
    steps.push(cfg.descriptionInstruction
        ? `${n++}. description には、${cfg.descriptionInstruction}（日本語 ${min}〜${max} 文字程度）`
        : `${n++}. description には、4 方向の画像全体から、この地点の環境を客観的に説明する（日本語 ${min}〜${max} 文字程度）。候補の一覧をなぞらず、地点全体の様子を書く。良い点と課題の両方があれば、偏らずに両方に触れる。`);
    parts.push(steps.join('\n'));
    const exampleOutput = dedupe
        ? { description: 'この地点の説明', ranking: ['C3', 'C1'], duplicates: ['C5'] }
        : { description: 'この地点の説明', ranking: ['C3', 'C1'] };
    parts.push([
        '【出力形式】',
        JSON.stringify(exampleOutput, null, 2),
        `出力は JSON オブジェクト 1 個のみ。キーは ${Object.keys(exampleOutput).join(' / ')} だけを使う。`,
    ].join('\n'));
    return parts.join('\n\n');
}
/** 選抜の JSON スキーマ (構造化出力)。重複除去をしない設定では duplicates を持たない。 */
export function buildStreetSurveySelectionJsonSchema(candidates, cfg) {
    const ids = candidates.map((c) => c.id);
    const idSchema = ids.length > 0 ? { type: 'string', enum: ids } : { type: 'string' };
    const dedupe = cfg.duplicateMode === 'same-object';
    return {
        type: 'object',
        properties: {
            description: { type: 'string' },
            ranking: { type: 'array', items: idSchema },
            ...(dedupe ? { duplicates: { type: 'array', items: idSchema } } : {}),
        },
        required: dedupe ? ['description', 'ranking', 'duplicates'] : ['description', 'ranking'],
    };
}
/**
 * 選抜に失敗したときの代替: 方向ごとに順番に 1 件ずつ取り、上限まで残す (方向の偏りを避ける)。
 */
export function fallbackStreetSurveySelection(candidates, totalMaxItems) {
    const byDirection = [0, 1, 2, 3].map((d) => candidates.filter((c) => c.imageIndex === d).sort((a, b) => a.directionRank - b.directionRank));
    const out = [];
    const limit = totalMaxItems ?? candidates.length;
    for (let round = 0; out.length < limit; round++) {
        let added = false;
        for (const list of byDirection) {
            const c = list[round];
            if (!c)
                continue;
            out.push(c);
            added = true;
            if (out.length >= limit)
                break;
        }
        if (!added)
            break;
    }
    return out;
}
/**
 * 選抜の応答を適用する。順位の ID から重複を除き (重複除去をする設定のときだけ)、応答で触れられなかった
 * 候補は失わないよう末尾に足す (方向ごとに均等な順)。上限があれば上位だけ残す。
 */
export function applyStreetSurveySelection(candidates, output, options) {
    const o = output && typeof output === 'object' ? output : null;
    const byId = new Map(candidates.map((c) => [c.id, c]));
    const description = o && typeof o.description === 'string' && o.description.trim() ? o.description.trim() : null;
    const ranking = Array.isArray(o?.ranking) ? o.ranking : null;
    const duplicates = new Set(options.duplicateMode === 'same-object' && Array.isArray(o?.duplicates)
        ? o.duplicates.filter((id) => typeof id === 'string' && byId.has(id))
        : []);
    const valid = !!o && ranking !== null && (candidates.length === 0 || description !== null);
    if (!valid)
        return { selected: [], description, valid: false, duplicateIds: [] };
    const ordered = [];
    for (const id of ranking) {
        if (typeof id !== 'string' || duplicates.has(id))
            continue;
        const c = byId.get(id);
        if (c && !ordered.includes(c))
            ordered.push(c);
    }
    // 1 件も有効な ID を返さなかった (候補はある) なら、応答は使えないものとして扱う。
    if (candidates.length > 0 && ordered.length === 0 && duplicates.size < candidates.length) {
        return { selected: [], description, valid: false, duplicateIds: [] };
    }
    const mentioned = new Set([...ordered.map((c) => c.id), ...duplicates]);
    for (const c of fallbackStreetSurveySelection(candidates, null)) {
        if (!mentioned.has(c.id))
            ordered.push(c);
    }
    return {
        selected: options.totalMaxItems !== null ? ordered.slice(0, options.totalMaxItems) : ordered,
        description,
        valid: true,
        duplicateIds: [...duplicates],
    };
}
/** 選抜後の候補から保存する結果を組み立てる (順位の振り直し・名称の補完・位置の抽出)。 */
export function buildStreetSurveyResult(selected, description, cfg, masters) {
    const items = [];
    const pins = [];
    selected.forEach((c, i) => {
        const rank = i + 1;
        const item = { rank };
        for (const axis of [cfg.anchor, ...cfg.attached]) {
            if (!axis)
                continue;
            const value = c.values[axis.codeField] ?? '';
            item[axis.codeField] = value;
            if (axis.nameField) {
                item[axis.nameField] = value
                    ? (streetSurveyRowOfValue(axis, masters, value)?.category ?? '')
                    : '';
            }
        }
        item[cfg.reasonKey] = c.reason;
        items.push(item);
        if (c.box2d && c.distanceMeters !== null) {
            pins.push({
                rank,
                imageIndex: c.imageIndex,
                box2d: c.box2d,
                distanceMeters: c.distanceMeters,
            });
        }
    });
    return {
        payload: {
            [STREET_SURVEY_DESCRIPTION_KEY]: description ?? '',
            [STREET_SURVEY_ITEMS_KEY]: items,
        },
        pins,
    };
}
/**
 * 検証用の出力定義 (C10)。元の出力フォーマットの分類軸・評価は残し、列だけを v2 の形にする
 * (自由記述の出力例は外す)。
 *
 * 集約スコアは外す (2026-09-16、docs/98 R-98 高 2)。v2 の各件には `assessment` / `score` 列が無いため、
 * 元の定義を残すと検証で総合スコアが常に 0 として保存されていた。机上調査は地点のスコアを持たず、
 * 分類の検出件数を集計するだけ。
 */
export function buildStreetSurveyOutputFormat(cfg, schema) {
    const itemFields = [
        { kind: 'text', key: 'rank', label: '順位', definition: '' },
    ];
    for (const axis of [cfg.anchor, ...cfg.attached]) {
        if (!axis)
            continue;
        itemFields.push({
            kind: 'code-list-string',
            key: axis.codeField,
            label: axis.label,
            definition: '',
            maxItems: 1,
            separator: ',',
            source: axis.masterId
                ? {
                    type: 'master',
                    masterId: axis.masterId,
                    valueFrom: axis.valueFrom ?? 'major-category-code',
                }
                : { type: 'inline', values: [] },
        });
        if (axis.nameField) {
            itemFields.push({
                kind: 'text',
                key: axis.nameField,
                label: `${axis.label}の名称`,
                definition: '',
            });
        }
    }
    itemFields.push({ kind: 'text', key: cfg.reasonKey, label: '根拠', definition: '' });
    const { customSchemaText: _omit, ...rest } = schema;
    void _omit;
    return {
        ...rest,
        aggregateScores: {},
        reasoningField: cfg.reasonKey,
        fields: [
            {
                kind: 'text',
                key: STREET_SURVEY_DESCRIPTION_KEY,
                label: '説明文',
                definition: '',
                optional: true,
                lengthMin: cfg.descriptionLength.min,
                lengthMax: cfg.descriptionLength.max,
            },
            {
                kind: 'object-array',
                key: STREET_SURVEY_ITEMS_KEY,
                label: '位置検出',
                minItems: 0,
                maxItems: cfg.totalMaxItems ?? cfg.perDirectionMaxItems * STREET_SURVEY_DIRECTION_LABELS.length,
                itemFields,
            },
        ],
    };
}
/** 検証ルール (許可値と件数、C10)。解析設定の検証ルールは通常投稿用なので使わない。 */
export function buildStreetSurveyValidatorRules(cfg, masters) {
    const rules = [
        {
            fieldPath: STREET_SURVEY_ITEMS_KEY,
            kind: 'maxItems',
            maxItems: cfg.totalMaxItems ?? cfg.perDirectionMaxItems * STREET_SURVEY_DIRECTION_LABELS.length,
        },
    ];
    for (const axis of [cfg.anchor, ...cfg.attached]) {
        if (!axis)
            continue;
        const allowed = streetSurveyAllowedValues(axis, masters);
        if (allowed.length > 0) {
            rules.push({ fieldPath: axis.codeField, kind: 'allowedValues', allowedValues: allowed });
        }
    }
    return rules;
}
/** 文字列の短い指紋 (FNV-1a 32bit、16 進 8 桁)。プロンプトの版の比較用で、暗号学的な用途ではない。 */
export function streetSurveyFingerprint(text) {
    let h = 0x811c9dc5;
    for (let i = 0; i < text.length; i++) {
        h ^= text.charCodeAt(i);
        h = Math.imul(h, 0x01000193) >>> 0;
    }
    return h.toString(16).padStart(8, '0');
}
/** プロンプトの指紋 (前方の方向別プロンプト + 候補なしの選抜プロンプト)。 */
export function streetSurveyPromptFingerprint(cfg, masters) {
    return streetSurveyFingerprint(buildStreetSurveyDirectionPrompt(cfg, masters, 0) +
        '\n---\n' +
        buildStreetSurveySelectionPrompt(cfg, masters, []));
}
/** 解析時の設定の記録を作る (実行結果の項目は呼出側で埋める)。 */
export function buildStreetSurveyExtractionRecord(cfg, masters, run) {
    return {
        pipelineVersion: STREET_SURVEY_PIPELINE_VERSION,
        anchorAxisKey: cfg.anchor?.key ?? '',
        anchorCodeField: cfg.anchor?.codeField ?? '',
        attachedAxisKeys: cfg.attached.map((a) => a.key),
        attachedCodeFields: cfg.attached.map((a) => a.codeField),
        perDirectionMaxItems: cfg.perDirectionMaxItems,
        totalMaxItems: cfg.totalMaxItems,
        itemsKey: STREET_SURVEY_ITEMS_KEY,
        reasonKey: cfg.reasonKey,
        masterDetail: cfg.masterDetail,
        duplicateMode: cfg.duplicateMode,
        includeMasterUsageRules: cfg.includeMasterUsageRules,
        promptFingerprint: streetSurveyPromptFingerprint(cfg, masters),
        ...run,
    };
}
/**
 * 記録された設定の違い (現在の設定と比べる、日本語、空なら同じ)。`masters` を渡すとプロンプトの内容の
 * 違い (プロンプトの文・マスタの変更) も比べる。
 */
export function diffStreetSurveyExtractionRecord(record, cfg, masters) {
    if (!cfg.enabled)
        return [];
    if (!record)
        return ['新しい解析方式で解析されていません'];
    const diffs = [];
    if (record.pipelineVersion !== STREET_SURVEY_PIPELINE_VERSION)
        diffs.push('解析方式の版');
    if (record.anchorCodeField !== (cfg.anchor?.codeField ?? ''))
        diffs.push('位置を検出する項目');
    const attached = cfg.attached.map((a) => a.codeField);
    if (record.attachedCodeFields.length !== attached.length ||
        record.attachedCodeFields.some((c, i) => c !== attached[i])) {
        diffs.push('各件に付ける項目');
    }
    if (record.perDirectionMaxItems !== cfg.perDirectionMaxItems)
        diffs.push('1 方向あたりの件数');
    if (record.totalMaxItems !== cfg.totalMaxItems)
        diffs.push('地点全体の件数');
    if ((record.duplicateMode ?? 'same-object') !== cfg.duplicateMode)
        diffs.push('重複の扱い');
    if (masters && diffs.length === 0) {
        if (record.promptFingerprint !== streetSurveyPromptFingerprint(cfg, masters)) {
            diffs.push('プロンプトの内容');
        }
    }
    return diffs;
}
//# sourceMappingURL=streetSurveyPipeline.js.map